'use client';

import { useEffect, useRef, useState } from 'react';

interface ScrollRevealBelowProps {
  children: React.ReactNode;
  className?: string;
}

export function ScrollRevealBelow({
  children,
  className,
}: ScrollRevealBelowProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [revealProgress, setRevealProgress] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion) {
      return;
    }

    const REVEAL_DISTANCE = 600; // px the element must travel into the viewport to fully reveal

    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      // How many px of the element are above the viewport bottom
      const visible = window.innerHeight - rect.top;
      setRevealProgress(Math.min(1, Math.max(0, visible / REVEAL_DISTANCE)));
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // check initial state
    return () => window.removeEventListener('scroll', onScroll);
  }, [prefersReducedMotion]);

  const skip = prefersReducedMotion || revealProgress >= 1;

  return (
    <div ref={ref} className={className}>
      <div
        style={
          skip
            ? undefined
            : {
                opacity: revealProgress,
                transform: `translateY(${(1 - revealProgress) * 40}px)`,
                transition: 'transform 0.2s ease-out',
              }
        }
      >
        {children}
      </div>
    </div>
  );
}
